import { useMemo, useEffect, useState } from 'react';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import TaxIdInput from '../../../molecules/TaxIdInput';
import ControlEditWithIcon from '../../../molecules/ControlEditWithIcon';
import { formatCurrency, calculateIntensity, getIntensityColor } from '../../../../utils/collateralFormatters';
import { collateralApi } from '../../../../services/collateralApi';

const VALIDATION_PROCESSES = [
    { key: 'validation_xml', label: 'XML' },
    { key: 'validation_invoice_pdf', label: 'NF PDF' },
    { key: 'validation_receipt_stub', label: 'Canhoto' },
    { key: 'validation_accepted_bill', label: 'Duplicata c/ Aceite' },
    { key: 'validation_contact', label: 'Validação Sacado' },
    { key: 'validation_purchase_contract', label: 'Contrato C/V' },
    { key: 'validation_debtor_notification', label: 'Cedente deve notificar sacados?' },
];

const CRITERIA_OPTIONS = [
    { value: 'DO_NOT_VALIDATE', label: 'Não validar' },
    { value: 'PRE_DISBURSEMENT', label: 'Pré-desembolso' },
    { value: 'POST_DISBURSEMENT', label: 'Pós-desembolso' },
];

const DEBTOR_NOTIFICATION_OPTIONS = [
    { value: 'DO_NOT_VALIDATE', label: 'Não' },
    { value: 'POST_DISBURSEMENT', label: 'Sim' },
];

const OPERATION_TYPE_OPTIONS = [
    { value: 'CESSAO', label: 'Cessão' },
    { value: 'CPRF', label: 'CPRF' },
];

const OBLIGATION_OPTIONS = [
    { value: 'WITHOUT_RECOURSE', label: 'Sem Regresso' },
    { value: 'WITH_RECOURSE', label: 'Com Regresso' },
    { value: 'NO_OBLIGATION', label: 'Sem Garantia' },
];

const CRITERIA_KEY_TO_TYPE: Record<string, string> = {
    validation_xml: 'XML', validation_invoice_pdf: 'INVOICE_PDF',
    validation_receipt_stub: 'RECEIPT_STUB', validation_accepted_bill: 'ACCEPTED_BILL',
    validation_contact: 'DRAWEE_CONFIRMATION', validation_purchase_contract: 'PURCHASE_CONTRACT',
    validation_sefaz: 'SEFAZ', validation_debtor_notification: 'DEBTOR_NOTIFICATION',
};

export interface ConfigureCriteriaFormProps {
    /** Summary shown at top */
    operationSummary: { operationCode: string; assignorName: string; totalAmount: number; totalReceivables: number };
    /** Fields */
    operationType: string;
    setOperationType: (v: string) => void;
    obligationType: string;
    setObligationType: (v: string) => void;
    traderName: string;
    setTraderName: (v: string) => void;
    assignorName?: string;
    setAssignorName?: (v: string) => void;
    assignorTaxId?: string;
    setAssignorTaxId?: (v: string) => void;
    validationConfig: Record<string, string>;
    setValidationConfig: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    analysisCriteria: 'Completa' | 'Amostragem';
    setAnalysisCriteria: (v: 'Completa' | 'Amostragem') => void;
    sampledTitles: number;
    setSampledTitles: (v: number) => void;
    /** Disables all fields when saving */
    disabled?: boolean;
    /** If provided, disables "Não validar" for DRAWEE_CONFIRMATION */
    draweeCannotBeDisabled?: boolean;
    /** Criteria keys that are locked (have progress) */
    lockedCriteria?: string[];
    /** Compact layout (grid 4 cols) for embedding in sections */
    compact?: boolean;
    /** Hide criteria (Completa/Amostragem) and intensity fields */
    hideCriteriaAndIntensity?: boolean;
}

const ConfigureCriteriaForm = ({
    operationSummary, operationType, setOperationType, obligationType, setObligationType,
    traderName, setTraderName, assignorName, setAssignorName, assignorTaxId, setAssignorTaxId,
    validationConfig, setValidationConfig,
    analysisCriteria, setAnalysisCriteria, sampledTitles, setSampledTitles,
    disabled = false, draweeCannotBeDisabled = false, lockedCriteria = [], compact = false,
    hideCriteriaAndIntensity = false,
}: ConfigureCriteriaFormProps) => {
    const totalTitles = operationSummary.totalReceivables || 1;
    const samplePercentage = totalTitles > 0 ? ((sampledTitles / totalTitles) * 100) : 0;

    const [managers, setManagers] = useState<string[]>([]);
    useEffect(() => {
        collateralApi.getInternalManagers().then(setManagers).catch(() => {});
    }, []);

    const intensity = useMemo(() => {
        const e = (k: string) => validationConfig[k] !== 'DO_NOT_VALIDATE';
        return calculateIntensity(
            { xml: e('validation_xml'), nf: e('validation_invoice_pdf'), canhoto: e('validation_receipt_stub'), duplicata: e('validation_accepted_bill') },
            operationSummary.totalReceivables || 0
        );
    }, [validationConfig, operationSummary.totalReceivables]);

    if (compact) {
        return (
            <div className="space-y-3">
                {/* Row 1: Tipo Operação, Obrigação, Trader */}
                <div className="grid grid-cols-3 gap-3">
                    <SelectTextField label="Tipo Operação" value={operationType} onChange={e => setOperationType(e.target.value)}
                        items={OPERATION_TYPE_OPTIONS} emptyLabel="Selecione..." disabled={disabled} />
                    <SelectTextField label="Obrigação" value={obligationType} onChange={e => setObligationType(e.target.value)}
                        items={OBLIGATION_OPTIONS} emptyLabel="Selecione..." disabled={disabled} />
                    <SelectTextField label="Trader" value={traderName} onChange={e => setTraderName(e.target.value)}
                        items={managers.map(name => ({ value: name, label: name }))} emptyLabel="Selecione..." disabled={disabled} />
                </div>
                {/* Row 2: Validações 1-4 */}
                <div className="grid grid-cols-4 gap-3">
                    {VALIDATION_PROCESSES.slice(0, 4).map(({ key, label }) => {
                        const isDrawee = key === 'validation_contact';
                        const isDebtor = key === 'validation_debtor_notification';
                        const criteriaType = CRITERIA_KEY_TO_TYPE[key];
                        const isLocked = lockedCriteria.includes(criteriaType);
                        const options = isDebtor ? DEBTOR_NOTIFICATION_OPTIONS : (isDrawee && draweeCannotBeDisabled) ? CRITERIA_OPTIONS.filter(o => o.value !== 'DO_NOT_VALIDATE') : CRITERIA_OPTIONS;
                        return <SelectTextField key={key} label={label} value={validationConfig[key]}
                            onChange={e => setValidationConfig(prev => ({ ...prev, [key]: e.target.value }))}
                            items={options} emptyValue="" emptyLabel="—" disabled={disabled || isLocked} />;
                    })}
                </div>
                {/* Row 3: Validações 5-8 */}
                <div className="grid grid-cols-4 gap-3">
                    {VALIDATION_PROCESSES.slice(4).map(({ key, label }) => {
                        const isDrawee = key === 'validation_contact';
                        const isDebtor = key === 'validation_debtor_notification';
                        const criteriaType = CRITERIA_KEY_TO_TYPE[key];
                        const isLocked = lockedCriteria.includes(criteriaType);
                        const options = isDebtor ? DEBTOR_NOTIFICATION_OPTIONS : (isDrawee && draweeCannotBeDisabled) ? CRITERIA_OPTIONS.filter(o => o.value !== 'DO_NOT_VALIDATE') : CRITERIA_OPTIONS;
                        return <SelectTextField key={key} label={label} value={validationConfig[key]}
                            onChange={e => setValidationConfig(prev => ({ ...prev, [key]: e.target.value }))}
                            items={options} emptyValue="" emptyLabel="—" disabled={disabled || isLocked} />;
                    })}
                </div>
                {/* Row 4: Critério de Análise + Intensidade */}
                {!hideCriteriaAndIntensity && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Critério:</span>
                        {(['Completa', 'Amostragem'] as const).map(opt => (
                            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" value={opt} checked={analysisCriteria === opt}
                                    onChange={() => setAnalysisCriteria(opt)} disabled={disabled}
                                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">{opt}</span>
                            </label>
                        ))}
                        {analysisCriteria === 'Amostragem' && (
                            <div className="flex items-center gap-2">
                                <input type="number" min={1} max={operationSummary.totalReceivables} value={sampledTitles}
                                    onChange={e => setSampledTitles(Math.min(Math.max(parseInt(e.target.value) || 1, 1), operationSummary.totalReceivables))}
                                    disabled={disabled}
                                    className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 dark:text-white" />
                                <span className="text-xs text-gray-500">/ {operationSummary.totalReceivables} ({samplePercentage.toFixed(0)}%)</span>
                            </div>
                        )}
                    </div>
                    {intensity && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getIntensityColor(intensity.level)}`}>
                            Intensidade: {intensity.level}
                        </span>
                    )}
                </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Operation summary */}
            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
                <p className="text-gray-700 dark:text-gray-300 font-medium">{operationSummary.operationCode} — {operationSummary.assignorName}</p>
                <div className="text-gray-500 dark:text-gray-400">{formatCurrency(operationSummary.totalAmount || 0)} · {operationSummary.totalReceivables || 0} títulos</div>
            </div>

            {/* Operation type, obligation */}
            {setAssignorName && (
                <TextEdit label="Nome do cedente" value={assignorName || ''} onChange={e => setAssignorName(e.target.value)} disabled={disabled} />
            )}
            {setAssignorTaxId && (
                <TaxIdInput type="PJ" label="CNPJ do cedente" value={assignorTaxId || ''} onChange={setAssignorTaxId} />
            )}
            <SelectTextField label="Tipo Operação" value={operationType} onChange={e => setOperationType(e.target.value)}
                items={OPERATION_TYPE_OPTIONS} emptyLabel="Selecione..." disabled={disabled} />
            <SelectTextField label="Obrigação" value={obligationType} onChange={e => setObligationType(e.target.value)}
                items={OBLIGATION_OPTIONS} emptyLabel="Selecione..." disabled={disabled} />

            {/* Trader */}
            <SelectTextField label="Trader" value={traderName}
                onChange={e => setTraderName(e.target.value)}
                items={managers.map(name => ({ value: name, label: name }))}
                emptyLabel="Selecione..." disabled={disabled} />

            {/* Validation processes */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Processos de Validação</label>
                <div className="space-y-3">
                    {VALIDATION_PROCESSES.map(({ key, label }) => {
                        const isDrawee = key === 'validation_contact';
                        const isDebtor = key === 'validation_debtor_notification';
                        const options = isDebtor ? DEBTOR_NOTIFICATION_OPTIONS : (isDrawee && draweeCannotBeDisabled)
                            ? CRITERIA_OPTIONS.filter(o => o.value !== 'DO_NOT_VALIDATE')
                            : CRITERIA_OPTIONS;
                        return (
                            <SelectTextField key={key} label={label} value={validationConfig[key]}
                                onChange={e => setValidationConfig(prev => ({ ...prev, [key]: e.target.value }))}
                                items={options} emptyValue="" emptyLabel="Selecione..." disabled={disabled} />
                        );
                    })}
                </div>
            </div>

            {/* Intensity preview */}
            {!hideCriteriaAndIntensity && intensity && (
                <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Intensidade:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getIntensityColor(intensity.level)}`}>
                        {intensity.level} (score: {intensity.score})
                    </span>
                </div>
            )}

            {/* Criteria + Sampling */}
            {!hideCriteriaAndIntensity && (
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Critério de Análise</label>
                <div className="flex items-center gap-4 mb-3">
                    {(['Completa', 'Amostragem'] as const).map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" value={opt} checked={analysisCriteria === opt}
                                onChange={() => setAnalysisCriteria(opt)} disabled={disabled}
                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                            <span className="text-sm text-gray-900 dark:text-gray-100">{opt}</span>
                        </label>
                    ))}
                </div>

                {analysisCriteria === 'Amostragem' && (
                    <div className="flex items-center gap-3">
                        <div style={{ minWidth: '11rem' }}>
                            <ControlEditWithIcon label="" text="títulos" minValue={1} maxValue={totalTitles}
                                value={sampledTitles} onIncrement={setSampledTitles} onDecrement={setSampledTitles} disabled={disabled} />
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{samplePercentage.toFixed(1)}%</span>
                    </div>
                )}
            </div>
            )}

            {draweeCannotBeDisabled && (
                <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <span className="text-sm text-amber-800 dark:text-amber-300">
                        Validação de sacado não pode ser desabilitada pois já existem confirmações em andamento.
                    </span>
                </div>
            )}
        </div>
    );
};

export default ConfigureCriteriaForm;
